/**
 * PKCE (Proof Key for Code Exchange) for OAuth 2.0 - required by Microsoft for SPA/cross-origin code redemption.
 * Generates code_verifier and code_challenge (S256).
 */

const MS_PKCE_VERIFIER_KEY = 'ms_oauth_code_verifier';

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generate a cryptographically random code_verifier (43-128 chars per RFC 7636).
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(array);
  } else {
    throw new Error('crypto.getRandomValues not available');
  }
  return base64UrlEncode(array.buffer);
}

/**
 * Compute code_challenge = base64url(SHA256(code_verifier)).
 */
export async function computeCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(hash);
}

/**
 * Generate verifier and challenge; store verifier in sessionStorage for use after redirect.
 */
export async function createPkceAndStore(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await computeCodeChallenge(codeVerifier);
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(MS_PKCE_VERIFIER_KEY, codeVerifier);
  }
  return { codeVerifier, codeChallenge };
}

/**
 * Read the stored code_verifier (call after redirect). Does not remove it.
 */
export function takeStoredCodeVerifier(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(MS_PKCE_VERIFIER_KEY);
}

/**
 * Remove the stored code_verifier. Call after successful token exchange.
 */
export function removeStoredCodeVerifier(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(MS_PKCE_VERIFIER_KEY);
  }
}
